"use client";
import { Badge, DescriptionList, DescriptionListItem, Grid } from '@develos/next-components';
import React, { useEffect, useRef, useState } from 'react';
import { VehicleRecordDetailsType } from '@/types';
import { useMetricLocalizations, useVehicleCategories } from '@/hooks';
import { badgeVariant, isOwerweightText, isSensorMissed, mapToFullPlatform } from '@/helpers';

type Props = {
  details: VehicleRecordDetailsType;
};

function parseCreatedToEpochSeconds(created: string): number {
  // "22:06:35 07-08-2025" -> "2025-08-07T22:06:35" (lokalna strefa)
  const createdISO = created.replace(
    /(\d{2}):(\d{2}):(\d{2}) (\d{2})-(\d{2})-(\d{4})/,
    "$6-$5-$4T$1:$2:$3"
  );
  const ms = new Date(createdISO).getTime();
  return Math.floor(ms / 1000);
}

const KeyValueContainer = ({ details }: Props) => {
  const { getCategoryDescription } = useVehicleCategories();
  const { getMetricLocalizationById } = useMetricLocalizations();

  const [timeLeft, setTimeLeft] = useState<number>(0);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    // jeżeli nie mamy ETA – nic nie odliczamy
    if (
      details.estimatedArrivalTimeToMetricPointInSeconds === null ||
      details.estimatedArrivalTimeToMetricPointInSeconds === undefined
    ) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      setTimeLeft(0);
      return;
    }

    // wyczyść ewentualny poprzedni interwał (przychodzi nowy details z SignalR)
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const createdInSeconds = parseCreatedToEpochSeconds(details.created);
    const arrivalInSeconds = createdInSeconds + details.estimatedArrivalTimeToMetricPointInSeconds;

    const tick = () => {
      const now = Math.floor(Date.now() / 1000);
      const diff = arrivalInSeconds - now;
      if (diff <= 0) {
        setTimeLeft(0);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }
      setTimeLeft(diff);
    };
    tick();
    intervalRef.current = window.setInterval(tick, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [details.created, details.estimatedArrivalTimeToMetricPointInSeconds]);

  return (
    <Grid gap={5} columns={1} mdColumns={12}>
      <div className="rm-relative rm-col-span-6">
        <DescriptionList>
          <DescriptionListItem label="Nr. rejestr.">{details.lpnumber}</DescriptionListItem>
          <DescriptionListItem label="Czas przejazdu">{details.created}</DescriptionListItem>
          <DescriptionListItem label="Lokalizacja">
            {mapToFullPlatform(details.platformId, getMetricLocalizationById(details.platformId))}
          </DescriptionListItem>
          <DescriptionListItem label="Waga">
            {details.weight} kg (<i>min: {details.minAvailableWeight} kg, max: {details.maxAvailableWeight} kg</i>)
          </DescriptionListItem>
          <DescriptionListItem label="Nacisk">
            Lewa: {details.weightLeft}, Prawa: {details.weightRight}
          </DescriptionListItem>
          {(details.weight - details.weightLimit) > 0
            ? <DescriptionListItem label="Wartość przekorczenia">{details.weight - details.weightLimit} kg</DescriptionListItem>
            : null}
          <DescriptionListItem label="Prędkość">{details.speed} km/h</DescriptionListItem>
          <DescriptionListItem label="Nr pasa">{details.lane}</DescriptionListItem>
        </DescriptionList>
      </div>

      <div className="rm-relative rm-col-span-6">
        <DescriptionList>
          <DescriptionListItem label="Nr. kolejny">{details.id}</DescriptionListItem>
          <DescriptionListItem label="Limit wagowy">{details.weightLimit} kg</DescriptionListItem>
          <DescriptionListItem label="Liczba osi">{details.axleCount}</DescriptionListItem>
          <DescriptionListItem label="Kategoria">{getCategoryDescription(details.categoryCost)}</DescriptionListItem>

          {details.estimatedArrivalTimeToMetricPointInSeconds != null
            ? (
              <DescriptionListItem label="Czas dojazdu">
                {timeLeft > 0
                  ? `${timeLeft} sek`
                  : `${details.estimatedArrivalTimeToMetricPointInSeconds} sek (czas oryginalny, minął)`}
              </DescriptionListItem>
            )
            : null}

          <DescriptionListItem label="Przeciążenie">
            <Badge variant={badgeVariant(details.isOverweight)}>
              {isOwerweightText(details.isOverweight)}
            </Badge>
          </DescriptionListItem>

          <DescriptionListItem label="Wysokość">
            <Badge variant={badgeVariant(details.isHighExceeded)}>
              {details.isHighExceeded ? 'Przekroczona wysokość' : 'Wysokość poprawna'}
            </Badge>
          </DescriptionListItem>

          <DescriptionListItem label="Ominięty czujnik">
            <Badge variant={badgeVariant(details.lane === 0)}>
              {isSensorMissed(details.lane === 0)}
            </Badge>
          </DescriptionListItem>
        </DescriptionList>
      </div>
    </Grid>
  );
};

export default KeyValueContainer;
